import { useRef, useEffect, useState } from 'react';
import { HDate } from '@hebcal/core';
import { PRIMARY_COLOR, PRIMARY_COLOR_LIGHT, PRIMARY_COLOR_DARK } from '../../constants/colors';

const HEBREW_MONTHS = [
  { name: 'Tishrei', theme: 'New Beginnings', color: PRIMARY_COLOR, 
    holidays: [
      { name: 'Rosh Hashanah', dayInMonth: 1 },
      { name: 'Yom Kippur', dayInMonth: 10 },
      { name: 'Sukkot', dayInMonth: 15 }
    ]
  },
  { name: 'Cheshvan', theme: 'Ordinary Time', color: '#717171', holidays: [] },
  { name: 'Kislev', theme: 'Light in Darkness', color: PRIMARY_COLOR_DARK, 
    holidays: [{ name: 'Chanukah', dayInMonth: 25 }]
  },
  { name: 'Tevet', theme: 'Winter Depth', color: '#222222', holidays: [] },
  { name: 'Shvat', theme: 'Nature Awakening', color: PRIMARY_COLOR_LIGHT, 
    holidays: [{ name: 'Tu B\'Shvat', dayInMonth: 15 }]
  },
  { name: 'Adar', theme: 'Joy & Celebration', color: PRIMARY_COLOR, 
    holidays: [{ name: 'Purim', dayInMonth: 14 }]
  },
  { name: 'Nisan', theme: 'Freedom & Renewal', color: PRIMARY_COLOR_DARK, 
    holidays: [{ name: 'Passover', dayInMonth: 15 }]
  },
  { name: 'Iyar', theme: 'Transition', color: PRIMARY_COLOR_LIGHT, 
    holidays: [{ name: 'Lag B\'Omer', dayInMonth: 18 }]
  },
  { name: 'Sivan', theme: 'Revelation', color: PRIMARY_COLOR, 
    holidays: [{ name: 'Shavuot', dayInMonth: 6 }]
  },
  { name: 'Tammuz', theme: 'Difficulty', color: '#717171', holidays: [] },
  { name: 'Av', theme: 'Mourning to Joy', color: PRIMARY_COLOR_DARK, 
    holidays: [{ name: 'Tisha B\'Av', dayInMonth: 9 }]
  },
  { name: 'Elul', theme: 'Introspection', color: PRIMARY_COLOR_LIGHT, holidays: [] }
];

function normalizeAngle(angle) {
  let normalized = angle % 360;
  if (normalized < 0) normalized += 360;
  return normalized;
}

export default function InteractiveOrrery({ currentAngle, onAngleChange, onHolidayJump, size = 300, showNextHoliday = false, isMobile: isMobileProp }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const currentAngleRef = useRef(currentAngle);
  const [isDragging, setIsDragging] = useState(false);
  const isHorizontalDragRef = useRef(false); // Track if this is a horizontal drag
  const dragStartRef = useRef({ x: null, y: null, angle: 0 });
  const [holidayDates2026, setHolidayDates2026] = useState({});

  useEffect(() => {
    currentAngleRef.current = currentAngle;
  }, [currentAngle]);

  const isMobile = isMobileProp !== undefined ? isMobileProp : (typeof window !== 'undefined' && window.innerWidth < 768);
  const centerX = size / 2;
  const centerY = size / 2;
  const sunSize = isMobile ? 20 : 28;
  const orbitRadius = isMobile ? size * 0.20 : size * 0.22; // Reduced from 0.25/0.28 to bring earth closer to sun
  const earthRadius = isMobile ? 8 : 12;
  const moonRadius = isMobile ? 3 : 5;
  const moonOrbitRadius = isMobile ? 14 : 20;

  const getPosition = (angle, radius) => ({
    x: centerX + radius * Math.cos(angle),
    y: centerY + radius * Math.sin(angle)
  });

  const degreesToRadians = (degrees) => degrees * Math.PI / 180;

  const normalized = normalizeAngle(currentAngle);
  const currentMonth = Math.floor(normalized / 30) % 12;
  
  // Earth position: moves 30 degrees per month (1/12th of full year)
  // currentAngle represents days: 0-360 for 12 months
  // Each month = 30 degrees, so earth angle = (month * 30) + (day within month)
  // But we want earth to move smoothly: 1 day = 1 degree of earth orbit
  const earthOrbitAngle = normalized; // 1 day = 1 degree of earth orbit (30 days = 30 degrees = 1 month)
  const earthYearAngle = degreesToRadians(earthOrbitAngle - 90);
  const earthPos = getPosition(earthYearAngle, orbitRadius);

  const earthToSunAngle = Math.atan2(centerY - earthPos.y, centerX - earthPos.x);

  // Moon orbits earth: 12 times per year (once per month)
  // Calculate moon's position relative to earth only
  // Over 30 days, moon completes 1 full orbit (360 degrees) relative to earth
  // So moon moves 12 degrees per day relative to earth
  // Negate the angle to make it rotate counter-clockwise instead of clockwise
  const angleInMonth = normalized % 30; // 0-30 degrees within current month
  const moonRelativeAngle = -(angleInMonth * 12); // -360 to 0 degrees relative to earth (counter-clockwise orbit)
  
  // On day 1 of month (angleInMonth = 0), moon should be between earth and sun
  // Moon between earth and sun means moon is on the line from sun to earth
  // The user said I shifted 90 degrees in the wrong direction
  // If I had +90 and it was wrong, I need to go 90 degrees clockwise from current
  // Current: moonOrbitAngle = moonRelativeAngle - 90, then moonLocalAngle = degreesToRadians(moonOrbitAngle - 90)
  // This gives: when angleInMonth=0, moonLocalAngle = degreesToRadians(-180) = pointing left
  // To move 90 degrees clockwise: add 90 to the final angle
  // So: moonLocalAngle = degreesToRadians(moonRelativeAngle - 90) (no double subtraction)
  // User said I shifted 90 degrees in wrong direction, need to move 90 degrees clockwise
  // Current: degreesToRadians(moonRelativeAngle - 90) gives -90 when moonRelativeAngle = 0
  // To move 90 degrees clockwise: add 90
  // So: degreesToRadians(moonRelativeAngle - 90 + 90) = degreesToRadians(moonRelativeAngle)
  // But that would put moon at 0 degrees (right) on day 1, not toward sun
  // Actually, "between earth and sun" means moon should be at -90 degrees (up/toward sun)
  // So I need: degreesToRadians(moonRelativeAngle - 90) but the user says it's 90 degrees off
  // If current is wrong by 90 degrees, and wrong direction means I went the wrong way
  // Then I should go the opposite direction: if I subtracted, I should add
  // So: degreesToRadians(moonRelativeAngle + 90 - 180) = degreesToRadians(moonRelativeAngle - 90)
  // Wait, that's the same. Let me try: degreesToRadians(moonRelativeAngle) which gives 0 on day 1
  // But that's pointing right, not toward sun. So maybe I need: degreesToRadians(moonRelativeAngle - 180)
  // That gives -180 on day 1, which points left. Still not right.
  // Let me try: the user said "90 degrees clockwise from current position"
  // If current is degreesToRadians(moonRelativeAngle - 90), and I add 90: degreesToRadians(moonRelativeAngle)
  // But that's 0 on day 1. Hmm.
  // Actually, maybe the issue is simpler: I should just add 90 to the current calculation
  // On day 1 (angleInMonth = 0), moon should be between earth and sun
  // "Between earth and sun" means moon is on the line from sun to earth
  // If earth is at top (-90 degrees), moon should be at -90 degrees relative to earth (pointing toward sun)
  // So when moonRelativeAngle = 0, moonLocalAngle should be -90 degrees = -π/2 radians
  const moonLocalAngle = degreesToRadians(moonRelativeAngle - 180); // -90 to 270 degrees, -90 = toward sun
  
  // Moon's absolute position = earth's position + moon's relative position
  const moonAbsoluteAngle = earthYearAngle + moonLocalAngle;
  const moonOffset = getPosition(moonAbsoluteAngle, moonOrbitRadius);
  const moonPos = {
    x: earthPos.x + moonOffset.x - centerX,
    y: earthPos.y + moonOffset.y - centerY
  };

  const moonToSunAngle = Math.atan2(centerY - moonPos.y, centerX - moonPos.x);

  const getBrightSidePath = (centerX, centerY, radius, toSunAngle) => {
    const startAngle = toSunAngle - Math.PI / 2;
    const endAngle = toSunAngle + Math.PI / 2;
    const sweepFlag = 1;
    
    const startX = centerX + Math.cos(startAngle) * radius;
    const startY = centerY + Math.sin(startAngle) * radius;
    const endX = centerX + Math.cos(endAngle) * radius;
    const endY = centerY + Math.sin(endAngle) * radius;
    
    return `M ${startX} ${startY} A ${radius} ${radius} 0 0 ${sweepFlag} ${endX} ${endY} L ${centerX} ${centerY} Z`;
  };

  // Helper function to get actual days in month for a given monthIndex
  const getDaysInMonth = (monthIndex) => {
    // Map monthIndex (0-11) to Hebrew month number (1-12)
    // Our array: Tishrei(0), Cheshvan(1), Kislev(2), Tevet(3), Shvat(4), Adar(5), Nisan(6), Iyar(7), Sivan(8), Tammuz(9), Av(10), Elul(11)
    const reverseMonthMap = {
      0: 7,   // Tishrei -> 7
      1: 8,   // Cheshvan -> 8
      2: 9,   // Kislev -> 9
      3: 10,  // Tevet -> 10
      4: 11,  // Shvat -> 11
      5: 12,  // Adar -> 12
      6: 1,   // Nisan -> 1
      7: 2,   // Iyar -> 2
      8: 3,   // Sivan -> 3
      9: 4,   // Tammuz -> 4
      10: 5,  // Av -> 5
      11: 6   // Elul -> 6
    };
    
    const hebrewMonth = reverseMonthMap[monthIndex];
    if (hebrewMonth === undefined) {
      console.error('Invalid monthIndex:', monthIndex);
      return 30; // Default fallback
    }
    
    // Get current Hebrew year
    const today = new HDate();
    const hebrewYear = today.getFullYear();
    
    // Create HDate for day 1 of that month to get actual daysInMonth
    try {
      const monthStart = new HDate(hebrewYear, hebrewMonth, 1);
      const daysInMonth = monthStart.daysInMonth();
      return daysInMonth;
    } catch (e) {
      console.error('Error getting daysInMonth:', e, { hebrewYear, hebrewMonth });
      return 30; // Default fallback
    }
  };

  // Fetch 2026 holiday dates from Hebcal API
  useEffect(() => {
    const fetchHolidayDates = async () => {
      try {
        // Fetch both major and minor holidays to get all holidays
        const response = await fetch('https://www.hebcal.com/hebcal?v=1&cfg=json&maj=on&min=on&year=2026');
        if (!response.ok) {
          throw new Error(`Hebcal API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Map API holiday names to our internal holiday names
        const holidayNameMap = {
          'Rosh Hashana 5787': 'Rosh Hashanah',  // Specific year to avoid matching LaBehemot
          'Rosh Hashana': 'Rosh Hashanah',  // API spells it without 'h' at the end
          'Rosh Hashanah': 'Rosh Hashanah',
          'Yom Kippur': 'Yom Kippur',
          'Sukkot': 'Sukkot',
          'Chanukah': 'Chanukah',
          'Hanukkah': 'Chanukah',
          'Tu B\'Shvat': 'Tu B\'Shvat',
          'Tu Bishvat': 'Tu B\'Shvat',
          'Purim': 'Purim',
          'Pesach': 'Passover',
          'Passover': 'Passover',
          'Lag B\'Omer': 'Lag B\'Omer',
          'Lag BaOmer': 'Lag B\'Omer',
          'Shavuot': 'Shavuot',
          'Shavuos': 'Shavuot',
          'Tish\'a B\'Av': 'Tisha B\'Av',  // API exact spelling with curly apostrophe
          'Tisha B\'Av': 'Tisha B\'Av',
          'Tish\'a BAv': 'Tisha B\'Av',
          'Tishah B\'Av': 'Tisha B\'Av',
          'Tisha BAv': 'Tisha B\'Av',
          'Erev Tish\'a B\'Av': 'Tisha B\'Av'  // Erev Tisha B'Av
        };
        
        const dates = {};
        if (data.items) {
          
          for (const item of data.items) {
            const apiName = item.title;
            
            // Skip "Rosh Hashana LaBehemot" - it's a different holiday (not Rosh Hashanah)
            if (apiName.includes('LaBehemot')) {
              continue;
            }
            
            // Skip "Rosh Hashana II" - we want the first day (1 Tishrei), not the second day (2 Tishrei)
            if (apiName.includes('Rosh Hashana') && (apiName.includes('II') || apiName.includes('2'))) {
              continue;
            }
            
            // Skip "Erev" holidays - we want the actual holiday date, not the eve
            // Exception: We'll handle Erev Tisha B'Av separately to get the actual Tisha B'Av date
            let isErevTishaBAv = false;
            if (apiName.includes('Erev') && apiName.includes('Tish') && apiName.includes('Av')) {
              isErevTishaBAv = true;
              // Don't skip, but we'll map it to Tisha B'Av and use the next day's date
            } else if (apiName.includes('Erev')) {
              continue; // Skip other Erev holidays
            }
            
            // Try exact match first
            let mappedName = holidayNameMap[apiName];
            
            // If no exact match, try case-insensitive and partial matching
            // Also normalize apostrophes (handle both straight ' and curly ')
            if (!mappedName) {
              const apiNameLower = apiName.toLowerCase();
              // Normalize apostrophes for comparison
              const normalizeApostrophes = (str) => str.replace(/[''']/g, "'");
              const apiNameNormalized = normalizeApostrophes(apiNameLower);
              
              // Special check for Tisha B'Av - check if normalized name contains "tish" and "av"
              if (!mappedName && apiNameNormalized.includes('tish') && apiNameNormalized.includes('av') && !apiNameNormalized.includes('tu b')) {
                    mappedName = 'Tisha B\'Av';
              }
              
              // Try matching against holidayNameMap keys
              if (!mappedName) {
                for (const [key, value] of Object.entries(holidayNameMap)) {
                  const keyNormalized = normalizeApostrophes(key.toLowerCase());
                  if (keyNormalized === apiNameNormalized || 
                      apiNameNormalized.includes(keyNormalized) || 
                      keyNormalized.includes(apiNameNormalized)) {
                    mappedName = value;
                    break;
                  }
                }
              }
            }
            
            // Special handling for Erev Tisha B'Av - use the next day (actual Tisha B'Av)
            if (isErevTishaBAv && mappedName === 'Tisha B\'Av' && item.date) {
              let itemDate = new Date(item.date);
              // Add 1 day to get to Tisha B'Av (the actual holiday)
              // Don't add another day for timezone - API dates are correct
              itemDate.setDate(itemDate.getDate() + 1);
              
              // Store as Tisha B'Av
              if (!dates['Tisha B\'Av'] || itemDate < dates['Tisha B\'Av']) {
                dates['Tisha B\'Av'] = itemDate;
              }
              continue; // Skip further processing for Erev
            }
            if (mappedName && item.date) {
              let itemDate = new Date(item.date);
              
              // For holidays that span multiple days, always use the earliest (first) date
              // Check if we already have a date for this holiday
              if (dates[mappedName]) {
                // If the new date is earlier, use it instead
                if (itemDate < dates[mappedName]) {
                  dates[mappedName] = itemDate;
                }
              } else {
                // First time seeing this holiday, store it
                dates[mappedName] = itemDate;
              }
            }
          }
        }
        
        setHolidayDates2026(dates);
      } catch (error) {
        console.error('Error fetching holiday dates from Hebcal API:', error);
      }
    };
    
    fetchHolidayDates();
  }, []);

  // Helper function to calculate angle from Hebrew date (accounting for 354-day year)
  const calculateAngleFromHebrewDate = (hebrewMonth, hebrewDay, hebrewYear) => {
    // Map Hebrew month (1-12, 1=Nisan) to our array index (0-11, 0=Tishrei)
    const monthMap = {
      7: 0,   // Tishrei
      8: 1,   // Cheshvan
      9: 2,   // Kislev
      10: 3,  // Tevet
      11: 4,  // Shvat
      12: 5,  // Adar
      1: 6,   // Nisan
      2: 7,   // Iyar
      3: 8,   // Sivan
      4: 9,   // Tammuz
      5: 10,  // Av
      6: 11   // Elul
    };
    
    const monthIndex = monthMap[hebrewMonth];
    if (monthIndex === undefined) return 0;
    
    // Calculate cumulative days from 1 Tishrei to the start of target month
    let cumulativeDays = 0;
    const today = new HDate();
    const currentYear = hebrewYear || today.getFullYear();
    
    // Start from Tishrei (month 7, index 0)
    for (let i = 0; i < monthIndex; i++) {
      const reverseMonthMap = {
        0: 7, 1: 8, 2: 9, 3: 10, 4: 11, 5: 12,
        6: 1, 7: 2, 8: 3, 9: 4, 10: 5, 11: 6
      };
      const monthNum = reverseMonthMap[i];
      const monthStart = new HDate(currentYear, monthNum, 1);
      cumulativeDays += monthStart.daysInMonth();
    }
    
    // Add days in current month (day - 1, since day 1 = 0 days into the month)
    cumulativeDays += (hebrewDay - 1);
    
    // Convert days to angle: 1 day = 360/354 degrees
    const degreesPerDay = 360 / 354;
    return normalizeAngle(cumulativeDays * degreesPerDay);
  };

  const jumpToMonth = (monthIndex) => {
    // Jump to the 15th of the month (full moon)
    const reverseMonthMap = {
      0: 7, 1: 8, 2: 9, 3: 10, 4: 11, 5: 12,
      6: 1, 7: 2, 8: 3, 9: 4, 10: 5, 11: 6
    };
    const hebrewMonth = reverseMonthMap[monthIndex];
    const today = new HDate();
    const hebrewYear = today.getFullYear();
    const newAngle = calculateAngleFromHebrewDate(hebrewMonth, 15, hebrewYear);
    
    onAngleChange(newAngle);
  };

  const jumpToHoliday = (monthIndex, dayInMonth) => {
    // Find the holiday name from the month and day
    const month = HEBREW_MONTHS[monthIndex];
    if (!month) {
      console.error('Invalid monthIndex:', monthIndex);
      return;
    }
    
    const holiday = month.holidays.find(h => h.dayInMonth === dayInMonth);
    if (!holiday) {
      console.error('Holiday not found for monthIndex:', monthIndex, 'dayInMonth:', dayInMonth);
      return;
    }
    
    // Get the 2026 date from the API data
    const targetGregorian = holidayDates2026[holiday.name];
    
    if (!targetGregorian) {
      
      // Try to find a close match (case-insensitive, partial match)
      const holidayNameLower = holiday.name.toLowerCase();
      const matchingKey = Object.keys(holidayDates2026).find(key => 
        key.toLowerCase() === holidayNameLower || 
        key.toLowerCase().includes(holidayNameLower) ||
        holidayNameLower.includes(key.toLowerCase())
      );
      
      if (matchingKey) {
        const matchedDate = holidayDates2026[matchingKey];
        // Calculate day offset from today
        const today = new HDate();
        const todayGregorian = today.greg();
        const dayOffset = Math.round((matchedDate.getTime() - todayGregorian.getTime()) / (1000 * 60 * 60 * 24));
        
        // Convert the Gregorian date to Hebrew date to get the actual month and day
        const targetHDate = new HDate(matchedDate);
        const actualHebrewMonth = targetHDate.getMonth();
        const actualHebrewDay = targetHDate.getDate();
        
        // Map Hebrew month (1-12, 1=Nisan) to our array index (0-11, 0=Tishrei)
        const monthMap = {
          7: 0,   // Tishrei
          8: 1,   // Cheshvan
          9: 2,   // Kislev
          10: 3,  // Tevet
          11: 4,  // Shvat
          12: 5,  // Adar
          1: 6,   // Nisan
          2: 7,   // Iyar
          3: 8,   // Sivan
          4: 9,   // Tammuz
          5: 10,  // Av
          6: 11   // Elul
        };
        
        const actualMonthIndex = monthMap[actualHebrewMonth];
        if (actualMonthIndex === undefined) {
          console.error('Invalid Hebrew month:', actualHebrewMonth, 'for date:', targetHDate.toString());
          // Fall back to using the orrery's monthIndex
          const reverseMonthMap = {
            0: 7, 1: 8, 2: 9, 3: 10, 4: 11, 5: 12,
            6: 1, 7: 2, 8: 3, 9: 4, 10: 5, 11: 6
          };
          const hebrewMonth = reverseMonthMap[monthIndex];
          const daysInMonth = getDaysInMonth(monthIndex);
          const newAngle = calculateAngleFromHebrewDate(hebrewMonth, dayInMonth, targetHDate.getFullYear());
          
          if (onHolidayJump) {
            onHolidayJump(dayOffset, newAngle);
          } else {
            onAngleChange(newAngle);
          }
          return;
        }
        
        // Calculate angle using the actual Hebrew month and day from the API date
        // Use the new calculation that accounts for 354-day year
        const newAngle = calculateAngleFromHebrewDate(actualHebrewMonth, actualHebrewDay, targetHDate.getFullYear());
        
        if (onHolidayJump) {
          onHolidayJump(dayOffset, newAngle);
        } else {
          onAngleChange(newAngle);
        }
        return;
        }
        
        return;
    }
    
    // Special handling for Chanukah: API returns date that converts to 23 Kislev,
    // but Chanukah should be on 25 Kislev (starts at sundown on 24 Kislev)
    let adjustedGregorian = new Date(targetGregorian);
    if (holiday.name === 'Chanukah') {
      adjustedGregorian.setDate(adjustedGregorian.getDate() + 1);
    }
    
    // Calculate day offset from today
    const today = new HDate();
    const todayGregorian = today.greg();
    const dayOffset = Math.round((adjustedGregorian.getTime() - todayGregorian.getTime()) / (1000 * 60 * 60 * 24));
    
    // Convert the adjusted Gregorian date to Hebrew date to get the actual month and day
    const targetHDate = new HDate(adjustedGregorian);
    const actualHebrewMonth = targetHDate.getMonth();
    const actualHebrewDay = targetHDate.getDate();
    
    // Map Hebrew month (1-12, 1=Nisan) to our array index (0-11, 0=Tishrei)
    const monthMap = {
      7: 0,   // Tishrei
      8: 1,   // Cheshvan
      9: 2,   // Kislev
      10: 3,  // Tevet
      11: 4,  // Shvat
      12: 5,  // Adar
      1: 6,   // Nisan
      2: 7,   // Iyar
      3: 8,   // Sivan
      4: 9,   // Tammuz
      5: 10,  // Av
      6: 11   // Elul
    };
    
    const actualMonthIndex = monthMap[actualHebrewMonth];
    if (actualMonthIndex === undefined) {
      console.error('Invalid Hebrew month:', actualHebrewMonth, 'for date:', targetHDate.toString());
      // Fall back to using the orrery's monthIndex
      const reverseMonthMap = {
        0: 7, 1: 8, 2: 9, 3: 10, 4: 11, 5: 12,
        6: 1, 7: 2, 8: 3, 9: 4, 10: 5, 11: 6
      };
      const hebrewMonth = reverseMonthMap[monthIndex];
      const daysInMonth = getDaysInMonth(monthIndex);
      const newAngle = calculateAngleFromHebrewDate(hebrewMonth, dayInMonth, targetHDate.getFullYear());
      
      if (onHolidayJump) {
        onHolidayJump(dayOffset, newAngle);
      } else {
        onAngleChange(newAngle);
      }
      return;
    }
    
    // Calculate angle using the actual Hebrew month and day from the API date
    // Use the new calculation that accounts for 354-day year
    const newAngle = calculateAngleFromHebrewDate(actualHebrewMonth, actualHebrewDay, targetHDate.getFullYear());
    
    // If onHolidayJump is provided, use it to set dayOffset directly
    // Otherwise fall back to onAngleChange (for backward compatibility)
    if (onHolidayJump) {
      onHolidayJump(dayOffset, newAngle);
    } else {
      onAngleChange(newAngle);
    }
  };

  // Drag handlers
  const handleDragStart = (clientX, clientY) => {
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      angle: normalizeAngle(currentAngleRef.current)
    };
    setIsDragging(false);
    isHorizontalDragRef.current = false;
  };

  const handleDragMove = (clientX, clientY) => {
    // Only process if we have a valid start position (indicates mouse/touch down occurred)
    if (dragStartRef.current.x === null && dragStartRef.current.y === null && !isDragging) {
      return; // No mouse/touch down event, ignore movement
    }
    
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);
    
    // Determine if this is primarily horizontal movement
    const isHorizontal = absDeltaX > absDeltaY;
    
    // If we haven't started dragging yet, check if it's horizontal
    if (!isDragging) {
      if (isHorizontal && absDeltaX > 5) {
        // Start horizontal drag
        setIsDragging(true);
        isHorizontalDragRef.current = true;
      } else if (!isHorizontal && absDeltaY > 5) {
        // This is vertical scrolling - don't handle, allow page scroll
        return;
      } else {
        // Not enough movement yet, wait
        return;
      }
    }
    
    // Only handle if we're in a horizontal drag
    if (isDragging && isHorizontalDragRef.current) {
      // Calculate angle change based on horizontal drag only
      // Same sensitivity as Calendar.jsx: 0.5 degrees per pixel
      const angleDelta = deltaX * 0.5;
      
      const newAngle = normalizeAngle(dragStartRef.current.angle + angleDelta);
      if (onAngleChange) {
        onAngleChange(newAngle);
      }
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    isHorizontalDragRef.current = false;
    // Reset start positions to indicate no active drag
    dragStartRef.current = { x: null, y: null, angle: 0 };
  };

  // Mouse handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseDown = (e) => {
      // Don't prevent default yet - wait to see if it's horizontal movement
      handleDragStart(e.clientX, e.clientY);
    };

    const handleMouseMove = (e) => {
      // Only process if mouse is down (isDragging or we have a valid start position)
      if (!isDragging && dragStartRef.current.x === null && dragStartRef.current.y === null) {
        return; // No mouse down, ignore movement
      }
      
      // Only prevent default if we're in a horizontal drag
      if (isDragging && isHorizontalDragRef.current) {
        e.preventDefault();
      }
      handleDragMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, onAngleChange]);

  // Touch handlers
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      // Don't prevent default yet - wait to see if it's horizontal movement
      const touch = e.touches[0];
      handleDragStart(touch.clientX, touch.clientY);
    };

    const handleTouchMove = (e) => {
      // Only prevent default if we're in a horizontal drag
      if (isDragging && isHorizontalDragRef.current) {
        e.preventDefault();
      }
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    };

    const handleTouchEnd = () => {
      handleDragEnd();
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd);
    container.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [isDragging, onAngleChange]);

  return (
    <div 
      ref={containerRef}
      className="relative"
      style={{ 
        width: size, 
        height: size,
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        touchAction: 'pan-y', // Allow vertical panning (scrolling)
        margin: 0,
        padding: 0,
        marginLeft: isMobile ? 0 : '-24px'
      }}
    >
      <svg ref={svgRef} width={size} height={size} style={{ display: 'block', margin: 0, padding: 0 }}>
        {/* Sun - Star shape */}
        <g transform={`translate(${centerX - sunSize / 2}, ${centerY - sunSize / 2}) scale(${sunSize / 93})`}>
          <svg width="93" height="97" viewBox="0 0 93 97" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M42.719 1.23596C44.8714 -0.412002 47.8615 -0.412 50.0139 1.23596L57.6948 7.11664C58.7828 7.94962 60.1231 8.38512 61.4929 8.35072L71.1634 8.10784C73.8734 8.03978 76.2924 9.7973 77.0651 12.3957L79.8225 21.668C80.2131 22.9814 81.0414 24.1215 82.1699 24.8988L90.1362 30.3865C92.3687 31.9244 93.2927 34.7681 92.3905 37.3244L89.1711 46.4466C88.7151 47.7387 88.7151 49.148 89.1711 50.4401L92.3905 59.5623C93.2927 62.1186 92.3687 64.9624 90.1362 66.5002L82.1699 71.9879C81.0414 72.7652 80.2131 73.9054 79.8225 75.2188L77.0651 84.491C76.2924 87.0894 73.8734 88.8469 71.1634 88.7789L61.4929 88.536C60.1231 88.5016 58.7828 88.9371 57.6948 89.7701L50.0139 95.6508C47.8615 97.2987 44.8714 97.2987 42.719 95.6508L35.0381 89.7701C33.9502 88.9371 32.6098 88.5016 31.24 88.536L21.5695 88.7789C18.8595 88.8469 16.4405 87.0894 15.6678 84.491L12.9104 75.2188C12.5198 73.9054 11.6915 72.7652 10.563 71.9879L2.59669 66.5002C0.364241 64.9624 -0.559744 62.1186 0.342424 59.5623L3.56176 50.4401C4.01778 49.148 4.01778 47.7387 3.56176 46.4466L0.342423 37.3244C-0.559745 34.7681 0.364239 31.9244 2.59668 30.3865L10.563 24.8988C11.6915 24.1215 12.5198 22.9814 12.9104 21.668L15.6678 12.3957C16.4405 9.7973 18.8595 8.03978 21.5695 8.10784L31.24 8.35072C32.6098 8.38512 33.9502 7.94962 35.0381 7.11664L42.719 1.23596Z" fill={PRIMARY_COLOR}/>
          </svg>
        </g>

        {/* Orbit */}
        <circle 
          cx={centerX} 
          cy={centerY} 
          r={orbitRadius} 
          fill="none" 
          stroke="#DDDDDD"
          strokeWidth="1.5"
          strokeDasharray="6,6"
        />

        {/* Month arcs and markers */}
        {HEBREW_MONTHS.map((month, index) => {
          const startAngle = -Math.PI / 2 + (index * 2 * Math.PI / 12);
          const endAngle = startAngle + (2 * Math.PI / 12);
          const startPos = getPosition(startAngle, orbitRadius);
          const endPos = getPosition(endAngle, orbitRadius);
          const midAngle = startAngle + Math.PI / 12;
          const labelPos = getPosition(midAngle, orbitRadius + (isMobile ? 35 : 45)); // Increased spacing to prevent overlap
          const isActive = index === currentMonth;
          
          // Get actual days in month for accurate holiday positioning
          const daysInMonth = getDaysInMonth(index);

          return (
            <g key={month.name}>
              {/* Month arc */}
              <path
                d={`M ${startPos.x} ${startPos.y} A ${orbitRadius} ${orbitRadius} 0 0 1 ${endPos.x} ${endPos.y}`}
                fill="none"
                stroke={isActive ? month.color : '#DDDDDD'}
                strokeWidth={isActive ? (isMobile ? 2.5 : 3) : (isMobile ? 1.5 : 2)}
                opacity={isActive ? 1 : 0.3}
                style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
                onClick={(e) => {
                  e.stopPropagation();
                  jumpToMonth(index);
                }}
              />

              {/* Month label */}
              <text
                x={labelPos.x}
                y={labelPos.y}
                fill={isActive ? month.color : '#717171'}
                fontSize={isActive ? (isMobile ? 12 : 15) : (isMobile ? 10 : 13)} // Increased font sizes
                fontWeight={isActive ? '600' : '500'}
                textAnchor="middle"
                style={{ cursor: 'pointer', userSelect: 'none', transition: 'all 0.3s ease', fontFamily: 'DM Sans, sans-serif' }}
                onClick={(e) => {
                  e.stopPropagation();
                  jumpToMonth(index);
                }}
              >
                {month.name}
                {isActive && (
                  <tspan
                    x={labelPos.x}
                    dy="1.4em" // Slightly more spacing for theme text
                    fontSize={isMobile ? 9 : 12} // Increased theme font size
                    fontWeight="400"
                    fill={month.color}
                    opacity="0.8"
                  >
                    {month.theme}
                  </tspan>
                )}
              </text>

              {/* Holiday markers */}
              {month.holidays.map((holiday, holidayIndex) => {
                // Use actual month length for accurate positioning
                // Day 1 should be at startAngle, last day should be at endAngle
                // Formula: dayFraction = (dayInMonth - 1) / daysInMonth, then angle = startAngle + dayFraction * (endAngle - startAngle)
                const dayFraction = (holiday.dayInMonth - 1) / daysInMonth; // Day 1 → 0, last day → ~1
                const holidayAngle = startAngle + dayFraction * (endAngle - startAngle);
                const holidayPos = getPosition(holidayAngle, orbitRadius);
                // Increase base offset and add more staggering to prevent overlap
                const baseOffset = isMobile ? 18 : 25;
                // Alternate between positive and negative offsets, with more spacing
                const staggerOffset = holidayIndex % 2 === 0 ? baseOffset : -baseOffset;
                // Position holiday labels between orbit and month labels to avoid overlap
                const holidayLabelPos = getPosition(holidayAngle, orbitRadius + staggerOffset);
                
                return (
                  <g 
                    key={holiday.name}
                    onClick={(e) => {
                      e.stopPropagation();
                      jumpToHoliday(index, holiday.dayInMonth);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle
                      cx={holidayPos.x}
                      cy={holidayPos.y}
                      r={isMobile ? 2.5 : 3.5}
                      fill={month.color}
                      stroke="#fff"
                      strokeWidth="1.5"
                    />
                    {showNextHoliday && (
                      <text
                        x={holidayLabelPos.x}
                        y={holidayLabelPos.y + (holidayIndex % 2 === 0 ? -2 : 2)} // Small vertical offset to prevent overlap
                        fill={month.color}
                        fontSize={isMobile ? 9 : 11} // Increased holiday label font size
                        fontWeight="500"
                        textAnchor="middle"
                        opacity="0.8"
                      >
                        {holiday.name}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Earth with bright/dark sides */}
        <g>
          <circle 
            cx={earthPos.x} 
            cy={earthPos.y} 
            r={earthRadius} 
            fill="#000000"
          />
          <path
            d={getBrightSidePath(earthPos.x, earthPos.y, earthRadius, earthToSunAngle)}
            fill={PRIMARY_COLOR_DARK}
          />
        </g>

        {/* Moon with bright/dark sides */}
        <g>
          <circle 
            cx={moonPos.x} 
            cy={moonPos.y} 
            r={moonRadius} 
            fill="#000000"
          />
          <path
            d={getBrightSidePath(moonPos.x, moonPos.y, moonRadius, moonToSunAngle)}
            fill="#CCCCCC"
          />
        </g>
      </svg>
    </div>
  );
}
